import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Pencil, Plus, Sparkles, Trash2, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Skeleton,
  Switch,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/utils';
import { EMPTY_DEFINITION, WorkflowBuilder } from './workflow-builder';
import { describeWorkflow } from './workflow-sentence';
import { workflowTemplates } from './workflow-templates';
import {
  LIMITS,
  lookupsApi,
  WORKFLOWS_KEY,
  workflowsApi,
  type Names,
  type Workflow,
  type WorkflowDefinition,
  type WorkflowRun,
} from './workflows-api';

const STATUS_TONE = {
  SCHEDULED: 'info',
  RUNNING: 'info',
  SUCCESS: 'success',
  SKIPPED: 'neutral',
  FAILED: 'danger',
} as const;

type ReasonKey =
  | 'conditions'
  | 'loop'
  | 'depth'
  | 'rate_limited'
  | 'feature_off'
  | 'subject_not_found'
  | 'disabled';
const REASONS = new Set<string>([
  'conditions',
  'loop',
  'depth',
  'rate_limited',
  'feature_off',
  'subject_not_found',
  'disabled',
]);

/**
 * "התהליכים שלי": רשימה עם מתג, ריצות אחרונות, תבניות מוכנות, בונה והיסטוריה.
 * רק הבעלים יוצר ומשנה (השרת אוכף); מנהל וצופה — קריאה בלבד.
 */
export function WorkflowsPage() {
  const { t } = useTranslation(['workflows', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const isOwner = can('OWNER');
  const [editing, setEditing] = useState<{ def: WorkflowDefinition; id?: string } | null>(null);
  const [history, setHistory] = useState<Workflow | null>(null);

  const list = useQuery({
    queryKey: WORKFLOWS_KEY,
    queryFn: ({ signal }) => workflowsApi.list(signal),
  });
  const jobTypes = useQuery({
    queryKey: ['job-type-templates'],
    queryFn: ({ signal }) => lookupsApi.jobTypes(signal),
  });
  const team = useQuery({ queryKey: ['team', 'lookup'], queryFn: ({ signal }) => lookupsApi.team(signal) });
  const names: Names = useMemo(
    () => ({
      jobTypes: Object.fromEntries((jobTypes.data ?? []).map((j) => [j.id, j.name])),
      users: Object.fromEntries((team.data ?? []).map((u) => [u.id, u.name])),
      suppliers: {},
    }),
    [jobTypes.data, team.data],
  );
  const templates = workflowTemplates(t, {
    firstJobTypeId: jobTypes.data?.[0]?.id,
    firstFieldUserId: team.data?.find((m) => m.role === 'FIELD')?.id,
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      workflowsApi.setEnabled(id, enabled),
    onSuccess: (w) => {
      toast.success(
        w.enabled ? t('page.enabled', { name: w.name }) : t('page.disabled', { name: w.name }),
      );
      void qc.invalidateQueries({ queryKey: WORKFLOWS_KEY });
    },
    onError: (err: unknown) =>
      toast.error(t('page.toggleFailed'), err instanceof ApiError ? err.message : undefined),
  });
  const remove = useMutation({
    mutationFn: (id: string) => workflowsApi.remove(id),
    onSuccess: () => {
      toast.success(t('page.deleted'));
      void qc.invalidateQueries({ queryKey: WORKFLOWS_KEY });
    },
    onError: (err: unknown) =>
      toast.error(t('page.toggleFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const count = list.data?.length ?? 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          isOwner ? (
            <Button
              onClick={() => setEditing({ def: EMPTY_DEFINITION })}
              disabled={count >= LIMITS.maxWorkflows}
            >
              <Plus aria-hidden />
              {t('page.new')}
            </Button>
          ) : null
        }
      />
      {!isOwner ? <p className="text-sm text-fg-muted">{t('page.viewOnly')}</p> : null}

      {list.isPending ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <EmptyState icon={Zap} title={t('page.empty')} />
      ) : (
        <>
          <p className="text-xs text-fg-subtle">
            {t('page.count', { count, max: LIMITS.maxWorkflows })}
          </p>
          <ul className="space-y-3">
            {list.data.map((w) => (
              <li key={w.id}>
                <Card>
                  <CardContent className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-fg">{w.name}</h2>
                        <p className="mt-1 text-sm text-fg-muted">
                          {describeWorkflow(t, w, names)}
                        </p>
                        {w.disabledReason && !w.enabled ? (
                          <p className="mt-1 text-xs text-danger">
                            {t('page.autoDisabled', { reason: w.disabledReason })}
                          </p>
                        ) : null}
                      </div>
                      <Switch
                        checked={w.enabled}
                        disabled={!isOwner || toggle.isPending}
                        onCheckedChange={(enabled) => toggle.mutate({ id: w.id, enabled })}
                        aria-label={t('page.toggleAria', { name: w.name })}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
                      <div className="flex flex-wrap items-center gap-1.5 text-2xs text-fg-subtle">
                        <span>{t('page.lastRuns')}:</span>
                        {w.recentRuns?.length ? (
                          w.recentRuns.map((r) => (
                            <Badge
                              key={r.id}
                              tone={STATUS_TONE[r.status]}
                              title={formatDateTime(r.createdAt)}
                            >
                              {t(`runs.status.${r.status}`)}
                            </Badge>
                          ))
                        ) : (
                          <span>{t('page.noRuns')}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setHistory(w)}>
                          <History aria-hidden />
                          {t('page.history')}
                        </Button>
                        {isOwner ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setEditing({
                                  id: w.id,
                                  def: {
                                    name: w.name,
                                    description: w.description ?? undefined,
                                    trigger: w.trigger,
                                    conditions: w.conditions,
                                    actions: w.actions,
                                  },
                                })
                              }
                            >
                              <Pencil aria-hidden />
                              {t('page.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(t('page.confirmDelete', { name: w.name })))
                                  remove.mutate(w.id);
                              }}
                            >
                              <Trash2 aria-hidden />
                              {t('page.delete')}
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      {isOwner && count < LIMITS.maxWorkflows ? (
        <section className="space-y-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-fg">
            <Sparkles className="size-4 text-accent" aria-hidden />
            {t('page.templates')}
          </h2>
          <ul className="grid gap-3 md:grid-cols-3">
            {templates.map((tpl) => (
              <li key={tpl.key}>
                <Card className="h-full">
                  <CardContent className="flex h-full flex-col gap-2">
                    <h3 className="text-sm font-medium text-fg">{tpl.def.name}</h3>
                    <p className="flex-1 text-xs text-fg-muted">
                      {describeWorkflow(t, tpl.def, names)}
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ def: tpl.def })}
                    >
                      {t('page.useTemplate')}
                    </Button>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {editing ? (
        <WorkflowBuilder
          initial={editing.def}
          workflowId={editing.id}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {history ? <RunHistory workflow={history} onClose={() => setHistory(null)} /> : null}
    </div>
  );
}

function RunHistory({ workflow, onClose }: { workflow: Workflow; onClose: () => void }) {
  const { t } = useTranslation('workflows');
  const runs = useQuery({
    queryKey: [...WORKFLOWS_KEY, workflow.id, 'runs'],
    queryFn: ({ signal }) => workflowsApi.runs(workflow.id, signal),
  });
  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('runs.title', { name: workflow.name })}</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          {runs.isPending ? (
            <Skeleton className="h-24" />
          ) : runs.isError ? (
            <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
          ) : runs.data.length === 0 ? (
            <p className="text-sm text-fg-muted">{t('runs.empty')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {runs.data.map((r) => (
                <RunRow key={r.id} run={r} />
              ))}
            </ul>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/** פירוט הריצה מהשרת (workflow_runs.actions): תוצאה לכל פעולה. */
const runDetail = z.object({
  actions: z.array(
    z.object({
      index: z.number(),
      status: z.enum(['done', 'skipped', 'failed', 'planned']),
      detail: z.string().optional(),
    }),
  ),
});

function RunRow({ run }: { run: WorkflowRun }) {
  const { t } = useTranslation('workflows');
  const detail = runDetail.safeParse(run.actions).data;
  return (
    <li className="space-y-1 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-fg-muted">{formatDateTime(run.createdAt)}</span>
        <Badge tone={STATUS_TONE[run.status]}>{t(`runs.status.${run.status}`)}</Badge>
      </div>
      {run.status === 'SCHEDULED' && run.scheduledFor ? (
        <p className="text-xs text-fg-muted">
          {t('runs.scheduledFor', { at: formatDateTime(run.scheduledFor) })}
        </p>
      ) : null}
      {run.reason && REASONS.has(run.reason) ? (
        <p className="text-xs text-fg-muted">{t(`runs.reason.${run.reason as ReasonKey}`)}</p>
      ) : null}
      {run.error ? (
        <p role="alert" className="text-xs text-danger">
          {run.error}
        </p>
      ) : null}
      {detail?.actions?.length ? (
        <ul className="text-xs text-fg-muted">
          {detail.actions.map((a) => (
            <li key={a.index}>
              {t(`runs.action.${a.status}`)}
              {a.detail ? ` · ${a.detail}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
